import { DragRow } from "@progress/kendo-angular-grid";
import { AttachmentStandardColumnIndicators, AttachmentStandardColumnNames } from "../../../enums/grid.enums";
import { IGridFlavor } from "../../../interfaces/igrid-flavor.interface";
import { GridConfig } from "../../grid/grid-config.model";
import { GridFlavorBase } from './grid-flavor-base';
import { FormField } from "../form-field.model";
import { ShortTextFieldType } from "../form-field-types";

export class AttachmentsGridFlavor extends GridFlavorBase implements IGridFlavor {

    constructor() {
        super();
        this.addAdditionalButtons();
    }
    public showEditingInfo: boolean = false;
    public rowHeightForVirtualScrolling: number = 0;
    public gridCommandColumnWidth: number = 150;
    public canAddNewRow: boolean = false;
    // By setting Kendo Grid's scrollable property to null we turn off virtual scrolling, but are still able to scroll
    // (Don't want to set it to "none" or the user would be unable to scroll)
    // This became neccessary because of strange behavior on the attachments grid in which if you had to scroll down to a
    // row you couldn't click it to open it for editing. 
    public scrollMode: string = null;

    public showGeocodeGrid(apiUrl: string, formFieldId: number): boolean {
        return false;
    }
    public addAnyVirtualColumns(gridConfig: GridConfig): void {
        this.addVirtualColumnDef(gridConfig, AttachmentStandardColumnNames.Title, 'Title', AttachmentStandardColumnIndicators.Title);
        this.addVirtualColumnDef(gridConfig, AttachmentStandardColumnNames.FileName, 'File Name', AttachmentStandardColumnIndicators.FileName);
        this.addVirtualColumnDef(gridConfig, AttachmentStandardColumnNames.FileType, 'File Type', AttachmentStandardColumnIndicators.FileType);
        this.addVirtualColumnDef(gridConfig, AttachmentStandardColumnNames.UploadInfo, 'Upload', AttachmentStandardColumnIndicators.UploadInfo);
        this.addVirtualColumnDef(gridConfig, AttachmentStandardColumnNames.DownloadInfo, 'Download', AttachmentStandardColumnIndicators.DownloadInfo);
        this.addVirtualColumnDef(gridConfig, AttachmentStandardColumnNames.Version, 'Version', AttachmentStandardColumnIndicators.Version);
        this.addVirtualColumnDef(gridConfig, AttachmentStandardColumnNames.IsLatest, 'Is Latest', AttachmentStandardColumnIndicators.IsLatest);
        this.addVirtualColumnDef(gridConfig, AttachmentStandardColumnNames.IsDisabled, 'Is Disabled', AttachmentStandardColumnIndicators.IsDisabled, true);
    }

    public addVirtualColumnDef(gridConfig: GridConfig, name: AttachmentStandardColumnNames, displayName: string, attachmentGridColumnIndicator: number, doNotDisplayInGrid: boolean = false) {
        // For VNEXT-2590 to fix multiplying virtual columns issue when CL targets an Attachment Grid
        // The problem was that the grid component's ngOnit() gets called whenever CL reevaluates a form and that calls addAnyVirtualColumns()
        // which calls the present method. Since gridConfig is initialized with virtual columns on the first such call subsequent calls kept adding
        // to them! So all that was needed was this simple to check to ensure if one exists it doesn't get added again.
        if (gridConfig.ColumnDefs.find(x => x.name == name)) return;

        const fullFakeId: number =
            gridConfig.GridFormField ?
                this.buildFakeId(gridConfig.GridFormField.id, attachmentGridColumnIndicator) :
                -999;                                                                                                       // the same approach is taken on the server
        let colDef = new FormField();
        colDef.name = name;
        colDef.displayName = displayName;
        colDef.id = fullFakeId; // this was added for VNEXT-1505 in which GridRow.RowDataJson is converted from keying values by childFormField name to childFormField id
        // since these are virtual columns need a fake id
        colDef.fieldDefinitionClassName = ShortTextFieldType;
        colDef.readOnly = true;
        colDef.doNotDisplayInGrid = doNotDisplayInGrid;
        gridConfig.ColumnDefs.push(colDef);
        gridConfig.HshFormFieldMNametoFormField[colDef.name] = colDef;
    }

    public usesButton(button: string, dataItem: any): boolean {
        const fakeId: string = `${this.buildFakeId(dataItem.__parentGridFieldId, AttachmentStandardColumnIndicators.IsDisabled)}`;
        let rowIsDisabled: boolean = dataItem?.__gridRow?.hshFormInstanceElementsById[fakeId]?.standinDisplayValue == "1";
        let initialResult: boolean = super.usesButton(button, rowIsDisabled);
        if (initialResult) {
            if (button == 'disable' && rowIsDisabled) {
                return false;
            } else if (button == 'restore' && !rowIsDisabled) {
                return false;
            } else if (button == 'download' && rowIsDisabled) {
                return false;
            } else if (button == 'upload' && rowIsDisabled) {
                return false;
            } else if (button == 'edit' && rowIsDisabled) {
                return false;
            } else {
                return true;
            }
        } else {
            return false;
        }
    }

    private buildFakeId(attachmentGridId: number, attachmentColumnIndicator: number): number {
        return (attachmentGridId * 10) + attachmentColumnIndicator;
    }

    public cannotMoveRowMessage(row: DragRow): string {
        let msg = null;
        let isLatest = row.dataItem[AttachmentStandardColumnNames.IsLatest] == 'true';
        if (!isLatest) {
            msg = "You cannot reposition an earlier version of an attachment, instead reposition the latest version.";
        }
        return msg;
    }

    private addAdditionalButtons() {
        this.gridCommandButtons = this.gridCommandButtons.concat(['disable', 'restore', 'download', 'upload']);
    }

}
